//@author devin
import { DataAccess } from "../dataaccess/data.access";
import { S25Util } from "../util/s25-util";
import { jSith } from "../util/jquery-replacement";
import { BpeService } from "../modules/bpe/bpe.service";
import { Cache, Invalidate } from "../decorators/cache.decorator";
import { Doc } from "../modules/system-settings/document-management/s25.document.const";
import { AccessLevel } from "../pojo/Fls";
import { Bpe } from "../modules/bpe/BpeI";
import { Proto } from "../pojo/Proto";
import { DocumentUtil } from "../modules/system-settings/document-management/s25.document.util";
import { ReportService } from "./report.service";

export class DocumentService {
    @Cache({ immutable: true, targetName: "DocumentService" })
    public static getDocumentList(type?: "event" | "reservation" | "invoice" | "organization", scope?: "subreport") {
        let url = "/document/list.json";
        if (type) url = url + "?type_name=" + type;
        if (scope) url = url + "?scope=" + scope;

        return DataAccess.get(DataAccess.injectCaller(url, "DocumentService.getDocumentList")).then(function (data: {
            document: DocumentListItem[];
        }) {
            return (data && data.document) || [];
        });
    }

    @Cache({ targetName: "DocumentService" })
    public static async getDocumentTemplateList() {
        const url = "/document/templates.json";
        const injected = DataAccess.injectCaller(url, "DocumentService.getDocumentTemplateList");
        const templates = await DataAccess.get<DocumentListItem[]>(injected);
        return templates || [];
    }

    @Cache({ targetName: "DocumentService", immutable: true })
    public static getDocument(documentId: number) {
        return DataAccess.get(
            DataAccess.injectCaller("/document/detail.json?itemId=" + documentId, "DocumentService.getDocument"),
        ).then(function (document: { document: Document }) {
            return document && document.document;
        });
    }

    @Invalidate({ serviceName: "DocumentService", methodName: "getDocumentList" })
    @Invalidate({ serviceName: "DocumentService", methodName: "getDocument", patternFunc: (args) => args[0] })
    public static setDocument(documentId: number, type: string, document: Partial<Document>) {
        return DataAccess.put<{ root: { itemId: number } }>(
            "/document/detail.json?itemId=" + (documentId || "") + "&type_name=" + type,
            {
                root: document,
            },
        );
    }

    @Invalidate({ serviceName: "DocumentService", methodName: "getDocumentList" })
    public static deleteDocument(documentId: number) {
        return DataAccess.delete("/document/detail.json?itemId=" + documentId);
    }

    public static getDocumentRun(documentRunId: number) {
        return DataAccess.get("/document/run.json?itemId=" + documentRunId);
    }

    public static massageDocumentRunText(text: string) {
        text = text || "";

        //The style attribute with the “\” at the beginning and the “\” at the end prevents the HTML from being translated.
        text = text.replace(/style=\\"(.*?)\\"/gi, 'style="$1"');

        //The hex color code “#000000bf” in the style attribute generated by the various table data options like “$pro.vars.occurrencesString”
        //causes the WS report to fail.  JReport doesn’t seem to do anything with it.
        text = text.replace(/#[0-9a-z]{2}([0-9a-z]{6})/gi, "$1");

        //The unit “rem” causes the report to fail.
        text = text.replace(/([\.\-]{0,1}[0-9]+[\.0-9]*)\s*rem;/gi, "$1em;");

        let promises: any = [];
        let imageReplacements: any = [];
        text.replace(/<img (.*?)src="(.*?)"/gi, function (match, prev: string, url: string) {
            try {
                url = S25Util.toSeries25DataUrl(url);
                promises.push(
                    S25Util.toDataUri(url).then(
                        function (dataUri: string) {
                            if (dataUri) {
                                imageReplacements.push({ url: url, newUrl: dataUri });
                            } else {
                                let urlObj = new URL(url);
                                let newUrl = DataAccess.getUrl(
                                    "/proxy/request?method=GET&serviceUrl=" + encodeURIComponent(url),
                                );
                                let hostOverride = "&headerOverrides=" + encodeURIComponent("host:" + urlObj.host);
                                return S25Util.toDataUri(newUrl, "https%3A%2F%2F", "http%3A%2F%2F").then(function (
                                    dataUri: string,
                                ) {
                                    if (dataUri) {
                                        imageReplacements.push({ url: url, newUrl: dataUri });
                                    } else {
                                        newUrl += hostOverride;
                                        return S25Util.toDataUri(newUrl, "https%3A%2F%2F", "http%3A%2F%2F").then(
                                            function (dataUri: string) {
                                                dataUri && imageReplacements.push({ url: url, newUrl: dataUri });
                                            },
                                        );
                                    }
                                });
                            }
                        },
                        function (err: any) {},
                    ),
                );
            } catch (e) {}
            return match;
        });

        return S25Util.all(promises).then(function () {
            jSith.forEach(imageReplacements, function (_: number, value: any) {
                text = text.replace(value.url, value.newUrl);
            });
            return text;
        });
    }

    public static setDocumentRun(
        documentId: number,
        headerText: string,
        bodyText: string,
        footerText: string,
        options?: DocumentLayoutOptions,
    ) {
        return S25Util.all({
            headerText: DocumentService.massageDocumentRunText(headerText),
            bodyText: DocumentService.massageDocumentRunText(bodyText),
            footerText: DocumentService.massageDocumentRunText(footerText),
        }).then(function (resp) {
            return DataAccess.post<{ root: { itemId: number } }>(
                "/document/run.json?itemId=" + (documentId ? documentId : ""),
                {
                    root: {
                        header_text: resp.headerText,
                        body_text: resp.bodyText,
                        footer_text: resp.footerText,
                        ...options,
                    },
                },
            );
        });
    }

    public static async getEventDocumentRunId(documentId: number, eventId: number, variables?: Bpe.BpeVars) {
        const document = await DocumentService.getDocument(documentId);
        return DocumentService.getEventDocumentRunIdFromText(
            document.header_text,
            document.body_text,
            document.footer_text,
            documentId,
            eventId,
            variables,
        );
    }

    public static async getEventDocumentRunIdFromText(
        header: string,
        body: string,
        footer: string,
        documentId: number,
        eventId: number,
        variables?: Bpe.BpeVars,
    ) {
        let eventData: Awaited<ReturnType<typeof BpeService.getEventData>>;
        if (!variables) {
            eventData = await BpeService.getEventData(eventId);

            const orgs = S25Util.array.forceArray(eventData?.organization);
            await BpeService.getDataForOrganizations(orgs);
        }

        const [headerText, bodyText, footerText] = await Promise.all([
            BpeService.getInterpolatedString(eventData, null, null, header, true, "document", variables),
            BpeService.getInterpolatedString(eventData, null, null, body, true, "document", variables),
            BpeService.getInterpolatedString(eventData, null, null, footer, true, "document", variables),
        ]);

        const documentRun = await DocumentService.setDocumentRun(
            documentId,
            headerText.interpolatedStr,
            bodyText.interpolatedStr,
            footerText.interpolatedStr,
        );

        return documentRun.root.itemId;
    }

    public static downloadSample(id: number, filename: string) {
        const objectType = 202;
        const link = document.createElement("a");
        link.href = DataAccess.composeUrl(
            `/fs/download/download_file.xml?shared=true&object_type=${objectType}&object_id=${id}&file_name=${encodeURIComponent(filename)}&display=inline`,
        );
        link.download = filename;
        link.click();
    }

    public static async getUsage(id: number): Promise<Proto.Result<DocumentUsage, unknown>> {
        const url = `/document/usage.json?itemId=${id}`;
        const injected = DataAccess.injectCaller(url, "DocumentService.getUsage");
        return S25Util.Maybe(DataAccess.get(injected));
    }

    public static async saveDocument(document: Document, original?: Document): Promise<number> {
        const create = !document.document_id;
        const [resp, error] = await S25Util.Maybe(
            DocumentService.setDocument(document.document_id, document.document_type, document),
        );
        if (error) return;

        if (DocumentUtil.hasReport(document)) {
            document.document_id = resp.root.itemId;
            if (create) {
                const [ok, err] = await S25Util.Maybe(this.createReport(document));
                if (err) {
                    DocumentService.handleReportError(document, null, err);
                    return;
                }
            } else if (document.report_id) {
                if (
                    document.document_name !== original.document_name ||
                    document.file_name !== original.file_name ||
                    document.apply_date_range !== original.apply_date_range
                ) {
                    // We only need to update the associated report if the document name or the report file name changed
                    const [ok, err] = await S25Util.Maybe(this.updateReport(document));
                    if (err) {
                        DocumentService.handleReportError(document, original, err);
                        return;
                    }
                }
            }
        }

        return resp.root.itemId;
    }

    public static async createReport(document: Document) {
        const shellReportId = Doc.type[document.document_type].reportId;
        const report = await ReportService.getReportMeta(shellReportId);
        const reportId = await ReportService.newReportId();
        report.status = "new";
        DocumentUtil.setReportApplyDateRange(document, report);
        for (let param of report.parameter) param.status = "new";
        for (let run of report.report_run) run.status = "new";
        report.report_id = reportId;
        report.report_name = document.document_name;
        report.content_disposition = `attachment; filename=${document.file_name}`;
        report.document_id = document.document_id;
        report.report_engine = "DM";
        await ReportService.updateReport(report);
    }

    public static async updateReport(document: Document) {
        // Update report with new document name and file name
        const report = await ReportService.getReportMeta(document.report_id);
        report.report_name = document.document_name;
        report.content_disposition = `attachment; filename=${document.file_name}`;
        DocumentUtil.setReportApplyDateRange(document, report);
        await ReportService.updateReport(report);
    }

    public static async handleReportError(document: Document, original: Document, error: any) {
        if (error?.error?.results?.error?.msg === "Report name already exists") {
            const msg = `A report with the same name already exists. Please choose a new document name and try again.`;
            S25Util.showError(msg);
            if (!original) {
                // Delete doc and let user try again
                await DocumentService.deleteDocument(document.document_id);
            } else {
                // Reset document
                await DocumentService.setDocument(document.document_id, document.document_type, original);
            }
            return;
        }
    }
}

export interface DocumentListItem {
    itemId: number;
    itemName: string;
    type: Doc.TypeId;
    accessLevel?: AccessLevel;
    reportId: number;
    description: string;
    sampleFilename?: string;
}

export interface Document extends DocumentLayoutOptions {
    document_id: number;
    document_name: string;
    header_text: string;
    body_text: string;
    footer_text: string;
    prim_foreground: string;
    prim_color: string;
    sec_color: string;
    sec_foreground: string;
    document_type: Doc.TypeId;
    report_id: number;
    file_name: string; // This is the output filename for the report
    description: string;
    sampleFilename?: string;
    document_scope: undefined | "subreport";
    subreports?: number[];
    apply_date_range: string;
}

export type DocumentLayoutOptions = {
    group_by?: "none" | "date" | "dateReservation" | "weeklyCalendar" | "monthlyCalendar" | "monthlyCalendarContinuous";
    sort_order?: string; // Comma separated string of elements
    page_break?: "none" | "group" | "reservation";
    margins?: string; // E.G. "0.25in 0.5in 0.17in 0.5in"
    font_size?: number; // em, one decimal
    page_orientation?: "portrait" | "landscape";
    page_header_height?: number; // One decimal
    page_footer_height?: number; // One decimal
};

export type DocumentUsage = DocumentUse[];
export type DocumentUse = Pick<Document, "document_id" | "document_name">;
